<script setup lang="ts">
import { onMounted, ref, type Ref, computed } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import type { User } from '@/models/User';
import { loadAdminUsers, patchUser, loadUserRoles } from '@/api/AdminUserApi';
import useAuthStore from '@/store/AuthStore';
import { requiredRule } from '@/helpers/FormRules';
import { Permission, Role } from '@/models/Roles';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();
const users: Ref<User[]> = ref([]);
const error = ref('');
const patchError = ref('');
const editDialog = ref(false);
const selectedUser: Ref<User | null> = ref(null);
const searchQuery = ref('');
const patchUserPassword = ref('');
const roles: Ref<Role[]> = ref([]);

const editUserValid = ref(true);

async function getUsers() {
  error.value = '';
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    users.value = await loadAdminUsers();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    error.value = err;
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

async function fetchRoles() {
  try {
    roles.value = await loadUserRoles();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
  }
}

async function saveChanges() {
  if (selectedUser.value) {
    loadingSpinnerStore.setShowLoadingOverlay(true);
    try {
      patchError.value = '';
      await patchUser(selectedUser.value);
      editDialog.value = false;
      patchUserPassword.value = '';
      getUsers();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      patchError.value = err;
    } finally {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  }
}

async function openEditDialog(user: User) {
  selectedUser.value = cloneDeep(user);
  editDialog.value = true;
  await fetchRoles();
}

function closeEditDialog() {
  patchError.value = '';
  editDialog.value = false;
}

const filteredUsers = computed(() => {
  const query = (searchQuery.value || '').toLowerCase();
  return users.value.filter((user) => {
    return (
      user.email.toLowerCase().includes(query) ||
      user.name.toLowerCase().includes(query)
    );
  });
});

const headers = computed(() => {
  const baseHeaders: object[] = [
    { title: $gettext('Email'), value: 'email', sortable: true },
    { title: $gettext('Name'), value: 'name', sortable: true },
    { title: $gettext('Role'), value: 'role', sortable: true },
  ];

  if (authStore.hasPermission(Permission.PATCH_USER)) {
    baseHeaders.push({
      title: $gettext('Actions'),
      key: 'actions',
      sortable: false,
      width: '100px', // Set a suitable width for the actions column
    });
  }

  return baseHeaders;
});

onMounted(() => {
  getUsers();
});
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Users') }}
          <v-btn
            size="small"
            class="mb-1 ml-4 gradient-oceanblue-whitesands"
            icon="mdi-cached"
            @click="getUsers"
          />
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-4">
        <v-row class="mt-12">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-text-field
              v-model="searchQuery"
              label="Search"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="error" class="mt-8">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-alert
              class="mt-8"
              border="top"
              icon="mdi-close-circle"
              border-color="error"
            >
              {{ error }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              items-per-page="25"
              :items="filteredUsers"
              :headers="headers"
            >
              <template #item.actions="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-pencil"
                        @click="openEditDialog(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Edit') }}
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-if="selectedUser" v-model="editDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $gettext('User') }}</v-card-title>
        <v-card-text>
          <v-form v-model="editUserValid">
            <v-text-field
              v-model="selectedUser.email"
              :label="$gettext('Email')"
              readonly
            ></v-text-field>
            <v-text-field
              v-model="selectedUser.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-autocomplete
              v-model="selectedUser.role"
              :items="roles"
              :label="$gettext('Role')"
            ></v-autocomplete>
          </v-form>
          <v-alert
            v-if="patchError"
            class="mt-8"
            border="top"
            icon="mdi-close-circle"
            border-color="error"
          >
            {{ patchError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeEditDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!editUserValid"
            @click="saveChanges"
            >{{ $gettext('Save') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
